import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './Combobox.module.scss';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxBaseProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  options: readonly ComboboxOption[];
  /** Nome acessivel do campo — e o que o leitor de tela anuncia. */
  label: string;
  /** Texto quando nada esta escolhido. Na selecao unica, e o proprio "sem filtro". */
  placeholder?: string;
  /** Mensagem quando o termo digitado nao casa com nenhuma opcao. */
  emptyMessage?: string;
  /** Complementa o filtro local com pesquisa no catálogo do servidor. */
  onSearchChange?: (term: string) => void;
  loading?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
}

export type ComboboxProps =
  | (ComboboxBaseProps & {
      multiple?: false;
      value: string;
      onChange: (value: string) => void;
    })
  | (ComboboxBaseProps & {
      multiple: true;
      /** Conjunto vazio significa "todas" — o campo nao filtra nada. */
      value: readonly string[];
      onChange: (value: string[]) => void;
    });

/** Compara ignorando caixa e acento: ninguem digita "Ângela" com o circunflexo. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Selecao com filtro por digitacao, de uma ou de varias opcoes.
 *
 * O `<select>` nativo resolve listas curtas; a partir de algumas dezenas de
 * nomes ele obriga a rolar procurando com o olho. Aqui a digitacao filtra, e a
 * lista mostra o que sobrou.
 *
 * Segue o padrao ARIA de combobox: `role="combobox"` no campo, `listbox` na
 * lista, navegacao por setas, `Enter` escolhe, `Escape` fecha e a opcao ativa e
 * anunciada por `aria-activedescendant`. Na selecao multipla a lista **nao
 * fecha** a cada escolha — quem marca tres profissionais nao quer reabrir o
 * campo tres vezes — e `Backspace` com o campo vazio remove a ultima ficha.
 *
 * A caixa e desenhada aqui, e nao reaproveitada do `Input`, porque as fichas da
 * selecao multipla moram **dentro** do campo, antes do texto digitado.
 */
export function Combobox({
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  options,
  label,
  placeholder = 'Selecione',
  emptyMessage = 'Nenhuma opção encontrada',
  onSearchChange,
  loading = false,
  errorMessage,
  disabled,
  className,
  ...rest
}: ComboboxProps) {
  const listId = useId();
  const campoRef = useRef<HTMLInputElement>(null);
  // `null` = ninguém está digitando; o campo mostra o que está escolhido.
  // `''` = a pessoa apagou o texto para procurar outra coisa.
  const [termo, setTermo] = useState<string | null>(null);
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);

  const multiplo = rest.multiple === true;
  const selecionados = multiplo ? [...(rest.value as readonly string[])] : [];
  const unico = multiplo ? '' : (rest.value as string);

  const rotuloDe = (valor: string): string =>
    options.find((opcao) => opcao.value === valor)?.label ?? valor;

  const filtradas = loading || errorMessage ? [] : termo
    ? options.filter((opcao) => normalizar(opcao.label).includes(normalizar(termo)))
    : options;

  /*
    O rótulo do escolhido continua no campo enquanto ninguém digita — inclusive
    com a lista aberta. Some só quando a pessoa começa a escrever, que é quando
    o campo deixa de mostrar a escolha e passa a mostrar a procura.
  */
  const textoDoCampo = termo ?? (multiplo ? '' : unico ? rotuloDe(unico) : '');

  const abrir = (): void => {
    if (disabled) return;
    setAberto(true);
    setAtivo(0);
  };

  const fechar = (): void => {
    setAberto(false);
    setTermo(null);
    onSearchChange?.('');
  };

  const escolher = (opcao: ComboboxOption): void => {
    if (multiplo) {
      const atuais = selecionados;
      const proximos = atuais.includes(opcao.value)
        ? atuais.filter((item) => item !== opcao.value)
        : [...atuais, opcao.value];
      (rest.onChange as (value: string[]) => void)(proximos);
      setTermo(null);
      setAtivo(0);
      campoRef.current?.focus();
      return;
    }

    (rest.onChange as (value: string) => void)(opcao.value);
    fechar();
  };

  const limpar = (): void => {
    if (multiplo) (rest.onChange as (value: string[]) => void)([]);
    else (rest.onChange as (value: string) => void)('');
    setTermo(null);
    onSearchChange?.('');
    campoRef.current?.focus();
  };

  const removerFicha = (valor: string): void => {
    (rest.onChange as (value: string[]) => void)(selecionados.filter((item) => item !== valor));
    campoRef.current?.focus();
  };

  const aoTeclar = (evento: KeyboardEvent<HTMLInputElement>): void => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      if (!aberto) return abrir();
      setAtivo((indice) => Math.min(indice + 1, filtradas.length - 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setAtivo((indice) => Math.max(indice - 1, 0));
    } else if (evento.key === 'Enter') {
      if (!aberto) return;
      evento.preventDefault();
      const opcao = filtradas[ativo];
      if (opcao) escolher(opcao);
    } else if (evento.key === 'Escape') {
      if (aberto) evento.stopPropagation();
      fechar();
    } else if (evento.key === 'Backspace' && multiplo && !termo && selecionados.length > 0) {
      // Sem isso, a unica forma de desfazer a ultima escolha e caçar o "x" dela.
      removerFicha(selecionados[selecionados.length - 1]!);
    }
  };

  const temSelecao = multiplo ? selecionados.length > 0 : unico !== '';

  return (
    <div
      className={cn(styles.root, className)}
      onBlur={(evento) => {
        if (evento.currentTarget.contains(evento.relatedTarget as Node | null)) return;
        fechar();
      }}
    >
      <div
        className={cn(styles.campo, disabled && styles.desabilitado)}
        onMouseDown={(evento) => {
          const alvo = evento.target as HTMLElement;
          // Os botões da caixa — a seta, o "x" e o de cada ficha — cuidam do
          // próprio clique; sem esta saída, o clique deles subiria até aqui e
          // reabriria a lista que a seta acabou de fechar.
          if (disabled || alvo === campoRef.current || alvo.closest('button')) return;

          // No resto da caixa, o clique cai no campo de texto: a faixa em volta
          // das fichas parecia inerte.
          evento.preventDefault();
          campoRef.current?.focus();
          abrir();
        }}
      >
        {multiplo
          ? selecionados.map((valor) => (
              <span key={valor} className={styles.ficha}>
                {rotuloDe(valor)}
                <button
                  type="button"
                  className={styles.remover}
                  onClick={() => removerFicha(valor)}
                  disabled={disabled}
                  aria-label={`Remover ${rotuloDe(valor)}`}
                >
                  <Icon as={X} size={16} />
                </button>
              </span>
            ))
          : null}

        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          aria-busy={loading}
          ref={campoRef}
          type="text"
          role="combobox"
          className={styles.entrada}
          aria-label={label}
          aria-expanded={aberto}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={aberto && filtradas[ativo] ? `${listId}-${ativo}` : undefined}
          // Com algo escolhido o espaço à direita não está vazio: some o
          // "Todos os profissionais" ao lado das fichas, que dizia o contrário
          // do que o campo mostrava.
          placeholder={temSelecao ? undefined : placeholder}
          value={textoDoCampo}
          disabled={disabled}
          onChange={(evento) => {
            setTermo(evento.target.value);
            onSearchChange?.(evento.target.value);
            setAberto(true);
            setAtivo(0);
          }}
          onFocus={(evento) => {
            // Seleciona o rótulo do escolhido: a primeira tecla digitada troca a
            // escolha em vez de emendar no nome que já estava lá.
            evento.target.select();
            abrir();
          }}
          onClick={(evento) => {
            // Mesmo cuidado do foco, para o clique num campo que já estava
            // focado — reabrir a lista não deve obrigar a apagar o nome à mão.
            if (termo === null) evento.currentTarget.select();
            abrir();
          }}
          onKeyDown={aoTeclar}
        />

        {temSelecao ? (
          <button
            type="button"
            className={styles.acao}
            onClick={limpar}
            disabled={disabled}
            aria-label={`Limpar ${label}`}
          >
            <Icon as={X} size={16} />
          </button>
        ) : null}

        {/*
          A seta é botão, não enfeite: ela é o convite visível a abrir a lista, e
          como `span` não fazia nada ao ser clicada. Fora da ordem de tabulação
          porque, pelo teclado, quem abre a lista é a seta para baixo no próprio
          campo.
        */}
        <button
          type="button"
          className={styles.chevron}
          onMouseDown={(evento) => {
            // `preventDefault` segura o foco onde está: sem isso o botão o
            // rouba do campo e o blur fecharia a lista no meio do clique.
            evento.preventDefault();

            if (aberto) {
              fechar();
              return;
            }

            // Só ao abrir o foco vai para o campo — focar ao fechar dispararia
            // o `onFocus`, que reabre a lista que o clique acabou de fechar.
            campoRef.current?.focus();
            abrir();
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-label={aberto ? `Fechar ${label}` : `Abrir ${label}`}
        >
          <Icon as={ChevronDown} size={16} className={cn(aberto && styles.chevronAberto)} />
        </button>
      </div>

      {aberto ? (
        <ul className={styles.lista} id={listId} role="listbox" aria-label={label}>
          {filtradas.length === 0 ? (
            <li className={styles.vazio} role="status">{loading ? 'Buscando opções...' : errorMessage || emptyMessage}</li>
          ) : (
            filtradas.map((opcao, indice) => {
              const marcada = multiplo
                ? selecionados.includes(opcao.value)
                : opcao.value === unico;

              return (
                <li
                  key={opcao.value}
                  id={`${listId}-${indice}`}
                  role="option"
                  aria-selected={marcada}
                  className={cn(styles.opcao, indice === ativo && styles.ativa)}
                  onMouseEnter={() => setAtivo(indice)}
                  onMouseDown={(evento) => {
                    // Só segura o foco no campo — o `mousedown` tiraria o foco
                    // do input e o blur fecharia a lista antes do clique.
                    // Escolher **aqui** seria pior: a lista se refaz no meio do
                    // clique, e o `click` que vem em seguida acaba caindo em
                    // outro elemento (a ficha recém-criada, por exemplo).
                    evento.preventDefault();
                  }}
                  onClick={(evento) => {
                    /*
                      Segura a ação padrão do clique. Quando o campo mora dentro
                      de um `<label>`, essa ação é "ativar o primeiro controle
                      rotulável do label" — e, com fichas na frente do input,
                      esse controle passa a ser o "x" da ficha recém-criada: o
                      clique que marcava o profissional desmarcava em seguida.
                    */
                    evento.preventDefault();
                    escolher(opcao);
                  }}
                >
                  {/* A marca de escolhido é ícone, não só cor de fundo. */}
                  <span className={styles.marca} aria-hidden="true">
                    {marcada ? <Icon as={Check} size={16} /> : null}
                  </span>
                  {opcao.label}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
